import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import { useState } from 'react'
import Image from 'next/image'

import { useMediaQuery } from '@mantine/hooks'
import Slider, { Settings } from 'react-slick'
import styles from './kdt-bebj-17th.module.scss'

interface IProjectOutputProps {
  contents: {
    imgUrlPc: string
    imgUrlM: string
    thumbnail: string
    title: string
  }[]
}

export const ProjectOutputSliderItem = ({ contents }: IProjectOutputProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const [activeIndex, setActiveIndex] = useState(0)
  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: true,
    centerPadding: '36%',
    touchMove: true,
    variableWidth: false,
    afterChange: setActiveIndex, // 현재 슬라이드 인덱스 업데이트
    responsive: [
      {
        breakpoint: 1930,
        settings: {
          dots: false,
          arrows: true,
          infinite: true,
          slidesToShow: 1,
          centerPadding: '32%',
        },
      },
      {
        breakpoint: 1500,
        settings: {
          dots: false,
          arrows: true,
          infinite: true,
          slidesToShow: 1,
          centerPadding: '30%',
        },
      },
      {
        breakpoint: 1023,
        settings: {
          dots: true,
          arrows: false,
          infinite: true,
          slidesToShow: 1,
          centerPadding: '24px',
        },
      },
    ],
  }

  const isInViewThumbnail = (idx: number, visibleCount: number = 2) => {
    const totalContents = contents.length
    // 현재 인덱스의 이전 n개, 현재, 다음 n개 인덱스를 계산 (순환 처리)
    const indices = []

    // 현재 인덱스 추가
    indices.push(activeIndex)

    // 이전 n개 인덱스 추가
    for (let i = 1; i <= visibleCount; i++) {
      indices.push((activeIndex - i + totalContents) % totalContents)
    }

    // 다음 n개 인덱스 추가
    for (let i = 1; i <= visibleCount; i++) {
      indices.push((activeIndex + i) % totalContents)
    }

    return indices.includes(idx)
  }

  return (
    <Slider {...settings}>
      {contents.map(({ imgUrlPc, imgUrlM, thumbnail, title }, idx) => (
        <div key={title} className="px-1.5 lg:px-0">
          <div
            className={`${styles.output_slide_mediaquery} relative flex w-full flex-col items-center justify-center`}
          >
            <div
              className="h-auto w-full overflow-hidden rounded-lg lg:shadow-[0px_0px_41px_0px_rgba(0,0,0,0),0px_0px_38px_0px_rgba(0,0,0,0.01),0px_0px_32px_0px_rgba(0,0,0,0.05),0px_0px_23px_0px_rgba(0,0,0,0.09),0px_0px_13px_0px_rgba(0,0,0,0.10)]"
              style={{
                position: 'relative',
                paddingBottom: '56%',
              }}
            >
              {/* 썸네일 표시, 화면에 보이는 썸네일만 렌더링 */}
              {isInViewThumbnail(idx) && (
                <img
                  src={thumbnail}
                  alt={title}
                  className="absolute left-0 top-0 z-10 h-full w-full object-cover transition-opacity duration-300"
                  style={{
                    opacity: activeIndex === idx ? 0 : 1,
                  }}
                />
              )}

              {activeIndex === idx && (
                <>
                  {/* 비디오 (slick-active일 때만 재생) */}
                  {activeIndex === idx && (
                    <div className="h-auto w-full object-contain">
                      <Image
                        src={isMobile ? imgUrlM : imgUrlPc}
                        layout="fill"
                        objectFit="cover"
                        className="left-0 top-0 w-full"
                        alt="-"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex w-full flex-row items-center justify-center px-4 pt-3 lg:px-6 lg:pt-8">
              <p className="text-sm font-bold text-label-normal lg:text-lg">{title}</p>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface IReviewProps {
  contents: {
    imgUrl: string
    titleM: string
    titlePc: string
    descM: string
    descPc: string
  }[]
}

export const ReviewSliderItem = ({ contents }: IReviewProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: false,
    slidesToShow: 3,
    slidesToScroll: 1,
    speed: 500,
    touchMove: true,
    className: 'carousel',
    variableWidth: true,
    responsive: [
      // {
      //   breakpoint: 1700,
      //   settings: {
      //     infinite: true,
      //     touchMove: true,
      //     centerMode: false,
      //     centerPadding: '0',
      //   },
      // },
      {
        breakpoint: 1300,
        settings: {
          infinite: true,
          slidesToShow: 1,
          dots: false,
          arrows: true,
          centerMode: false,
          centerPadding: '0',
          variableWidth: true,
        },
      },
      {
        breakpoint: 1023,
        settings: {
          infinite: true,
          slidesToShow: 1,
          dots: true,
          arrows: false,
          centerMode: false,
          centerPadding: '0',
          variableWidth: true,
        },
      },
    ],
  }
  return (
    <Slider {...settings}>
      {contents.map(({ imgUrl, titleM, titlePc, descM, descPc }) => (
        <div key={titleM} className="pl-4 lg:pl-6">
          <div className="h-[240px] w-full overflow-hidden rounded-lg bg-[#404040] p-4 lg:py-[30px]">
            <div className="flex w-full flex-row items-center gap-4">
              <div className="h-12 w-12 shrink-0">
                <Image src={imgUrl} layout="fill" objectFit="cover" className="!relative" alt="-" />
              </div>
              <div className="flex-1 whitespace-pre-line text-lg font-bold leading-normal text-white">
                {isMobile ? titleM : titlePc}
              </div>
            </div>
            <div className="mt-4 border-t border-[#525252] pt-4">
              <p className="whitespace-pre-line text-base leading-normal text-label-assistive">
                {isMobile ? descM : descPc}
              </p>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface IBenefitCarouselProps {
  contents: {
    imgUrlM: string
    imgUrlPc: string
    title: string
    desc: string
    option?: string
  }[]
}

export const BenefitSliderItem = ({ contents }: IBenefitCarouselProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    touchMove: true,
    variableWidth: true,
    centerMode: false,
    centerPadding: '0',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          infinite: true,
          slidesToShow: 1,
          dots: true,
          arrows: false,
          centerMode: false,
          centerPadding: '0',
          variableWidth: true,
        },
      },
    ],
  }
  return (
    <Slider {...settings}>
      {contents.map(({ desc, title, imgUrlM, imgUrlPc, option }) => (
        <div key={title} className="w-full lg:pb-[77px]">
          <div className="pl-4 lg:pl-6">
            <div className="mb-[18px] overflow-hidden rounded-md bg-gray-900 lg:mb-0 lg:rounded-b-none lg:rounded-t-lg lg:bg-black">
              <img
                src={isMobile ? imgUrlM : imgUrlPc}
                className="block h-full w-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="flex flex-col gap-2 lg:h-[180px] lg:rounded-b-lg lg:bg-gray-900 lg:p-6">
              <p className="text-base font-bold text-white lg:text-xl">{title}</p>
              <p className="whitespace-pre-wrap text-base leading-normal text-label-assistive lg:text-white">
                {desc}
              </p>
              {option && <p className="text-xs text-label-alternative lg:text-sm">{option}</p>}
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}
